import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import SEO from '@/components/common/SEO';

const M = '#06060c';
const G = '#d4a843';
const IN = '#0ea5e9';
const BR = 'rgba(255,255,255,0.07)';
const CA = 'rgba(255,255,255,0.04)';
const TX = 'rgba(255,255,255,0.88)';
const MU = 'rgba(255,255,255,0.45)';

interface Lesson {
  lesson_id: number;
  title: string;
  core_concepts: string[];
  passing_score: number;
  learning_objective: string;
}
interface Module {
  module_id: number;
  title: string;
  certificate: string;
  lessons: Lesson[];
}
interface DegreeClass {
  id: string;
  name: string;
  knowledge_context: string;
  module_plan: Module[];
}
interface ProgressState {
  current_unit: number;
  current_lesson: number;
  total_lessons_completed: number;
  completed_lessons?: Record<string, boolean>;
}

function Loader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: M }}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
        style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid rgba(212,168,67,0.15)`, borderTopColor: G }}
      />
    </div>
  );
}

export default function UniversityDegreePage() {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isLoading: authLoading } = useAuth();

  const [degree, setDegree] = useState<DegreeClass | null>(null);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [mastery, setMastery] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openModule, setOpenModule] = useState<number | null>(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 820);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate('/signin', { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user || !classId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const { data: cls } = await supabase
          .from('classes')
          .select('id, name, knowledge_context, module_plan')
          .eq('id', classId)
          .single();

        if (cancelled) return;
        setDegree(cls as DegreeClass);

        const { data: enrol } = await supabase
          .from('class_enrollments')
          .select('mastery_score, progress_state')
          .eq('student_id', user!.id)
          .eq('class_id', classId)
          .maybeSingle();

        if (cancelled) return;
        if (enrol) {
          setMastery(Number(enrol.mastery_score) || 0);
          setProgress(enrol.progress_state as ProgressState);
        }
      } catch (err) {
        console.error('DegreePage load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user, classId]);

  if (authLoading || loading) return <Loader />;
  if (!degree) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: M, color: MU }}>
      {t('university.degree.notFound', 'Degree not found.')}
    </div>
  );

  const totalLessons = degree.module_plan.reduce((s, m) => s + m.lessons.length, 0);
  const completedLessons = progress?.total_lessons_completed || 0;
  const progressPct = totalLessons > 0 ? Math.min(Math.round((completedLessons / totalLessons) * 100), 100) : 0;
  const currentUnit = progress?.current_unit || 1;
  const currentLesson = progress?.current_lesson || 1;
  const completedMap = progress?.completed_lessons || {};

  function isLessonUnlocked(modId: number, lessonId: number): boolean {
    if (modId < currentUnit) return true;
    if (modId === currentUnit) return lessonId <= currentLesson;
    return false;
  }
  function isLessonCompleted(modId: number, lessonId: number): boolean {
    // Check map first for exact status
    const key = `${modId}-${lessonId}`;
    if (completedMap[key]) return true;

    // Fallback to sequential logic
    if (modId < currentUnit) return true;
    if (modId === currentUnit && lessonId < currentLesson) return true;
    return false;
  }

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO title={degree?.name || "Degree"} noindex />

      {/* Hero header */}
      <div style={{
        background: 'linear-gradient(160deg, rgba(14,165,233,0.12) 0%, rgba(212,168,67,0.06) 100%)',
        borderBottom: `1px solid ${BR}`,
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '32px 20px 40px' : '48px 24px 40px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'none',
              border: 'none',
              color: MU,
              cursor: 'pointer',
              fontSize: 11,
              marginBottom: 24,
              padding: 0,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            ← {t('university.degree.backToDashboard', 'BACK TO DASHBOARD')}
          </button>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? 32 : 24
          }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 11, color: G, letterSpacing: '2px', fontWeight: 800, marginBottom: 12 }}>
                {t('university.degree.eyebrow', 'DEGREE PROGRAMME')}
              </p>
              <h1 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 900, margin: '0 0 16px', letterSpacing: '-0.8px', lineHeight: 1.1 }}>
                {degree.name}
              </h1>
              <p style={{ color: MU, fontSize: 14, maxWidth: 640, lineHeight: 1.7, margin: 0, fontWeight: 500 }}>
                {degree.knowledge_context}
              </p>
            </div>

            <motion.button
              whileHover={{ scale: 1.04, boxShadow: '0 8px 32px rgba(212,168,67,0.35)' }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(`/learn/${classId}`)}
              style={{
                background: `linear-gradient(135deg, ${G}, #b8912e)`,
                color: '#06060c', border: 'none', borderRadius: 14,
                padding: '16px 36px', fontSize: 15, fontWeight: 800, cursor: 'pointer',
                whiteSpace: 'nowrap', flexShrink: 0,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              {completedLessons === 0
                ? 'Start Degree →'
                : 'Continue Learning →'}
            </motion.button>
          </div>

          {/* Progress bar */}
          <div style={{ marginTop: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 12, fontWeight: 700 }}>
              <span style={{ color: MU, letterSpacing: '0.5px' }}>
                {completedLessons}/{totalLessons} LESSONS COMPLETE
              </span>
              <span style={{ color: completedLessons > 0 ? G : MU }}>{progressPct}%</span>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 100, height: 8, overflow: 'hidden' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  height: '100%', borderRadius: 100,
                  background: `linear-gradient(90deg, ${IN}, ${G})`,
                }}
              />
            </div>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 12 : 24,
              marginTop: 16
            }}>
              <span style={{ fontSize: 13, color: TX, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: G }}>🎯</span> Mastery: {mastery}%
              </span>
              <span style={{ fontSize: 13, color: TX, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: IN }}>📦</span> {degree.module_plan.length} Modules
              </span>
              <span style={{ fontSize: 13, color: TX, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#4ade80' }}>🏆</span> {degree.module_plan.length} Certificates
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Module accordion */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '32px 16px 80px' : '40px 24px 80px' }}>
        <h2 style={{ fontWeight: 800, fontSize: isMobile ? 20 : 24, marginBottom: 24, letterSpacing: '-0.5px' }}>
          Curriculum
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {degree.module_plan.map((mod) => {
            const isOpen = openModule === mod.module_id;
            const modCompleted = mod.module_id < currentUnit;
            const modActive = mod.module_id === currentUnit;
            const modLocked = mod.module_id > currentUnit;

            return (
              <motion.div
                key={mod.module_id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: mod.module_id * 0.06 }}
                style={{
                  background: modActive ? 'rgba(255,255,255,0.03)' : CA,
                  border: `1px solid ${modActive ? 'rgba(212,168,67,0.35)' : BR}`,
                  borderRadius: 20, overflow: 'hidden',
                  opacity: modLocked ? 0.6 : 1,
                  transition: 'border-color 0.2s, background 0.2s'
                }}
              >
                {/* Module header */}
                <button
                  onClick={() => !modLocked && setOpenModule(isOpen ? null : mod.module_id)}
                  style={{
                    width: '100%', background: 'none', border: 'none',
                    padding: isMobile ? '20px' : '24px', cursor: modLocked ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 20, textAlign: 'left',
                  }}
                >
                  {/* Status icon */}
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: modCompleted
                      ? 'rgba(74,222,128,0.15)'
                      : modActive
                        ? 'rgba(212,168,67,0.15)'
                        : 'rgba(255,255,255,0.06)',
                    border: `1.5px solid ${modCompleted ? '#4ade80' : modActive ? G : BR}`,
                    fontSize: 16, fontWeight: 700
                  }}>
                    {modCompleted ? '✓' : modActive ? '▶' : '🔒'}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: isMobile ? 15 : 18, color: modLocked ? MU : TX, lineHeight: 1.3 }}>
                      {mod.title}
                    </div>
                    <div style={{ fontSize: 13, color: MU, marginTop: 4, fontWeight: 500 }}>
                      {mod.lessons.length} {t('university.degree.lessons', 'lessons')} · 🏆 {mod.certificate}
                    </div>
                  </div>

                  {!modLocked && (
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      style={{ color: MU, fontSize: 20 }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                    </motion.span>
                  )}
                </button>

                {/* Lessons */}
                <AnimatePresence>
                  {isOpen && !modLocked && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div style={{ borderTop: `1px solid ${BR}`, padding: '8px 0 24px' }}>
                        {mod.lessons.map((lesson) => {
                          const unlocked = isLessonUnlocked(mod.module_id, lesson.lesson_id);
                          const completed = isLessonCompleted(mod.module_id, lesson.lesson_id);
                          const active = mod.module_id === currentUnit && lesson.lesson_id === currentLesson;

                          return (
                            <motion.button
                              key={lesson.lesson_id}
                              whileHover={unlocked && !isMobile ? { x: 4, background: 'rgba(255,255,255,0.03)' } : {}}
                              onClick={() => unlocked && navigate(`/learn/${classId}?module=${mod.module_id}&lesson=${lesson.lesson_id}`)}
                              style={{
                                width: '100%', background: active ? 'rgba(212,168,67,0.08)' : 'transparent',
                                border: 'none', padding: isMobile ? '12px 20px' : '15px 24px 15px 86px',
                                cursor: unlocked ? 'pointer' : 'default',
                                display: 'flex', alignItems: 'flex-start', gap: 16,
                                textAlign: 'left', transition: 'background 0.2s',
                              }}
                            >
                              {/* Lesson status dot */}
                              <div style={{
                                width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                                background: completed ? '#4ade80' : active ? G : 'rgba(255,255,255,0.1)',
                                border: `1.5px solid ${completed ? '#4ade80' : active ? G : BR}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, color: completed ? '#06060c' : 'transparent',
                              }}>
                                {completed ? '✓' : ''}
                              </div>

                              <div style={{ flex: 1, opacity: unlocked ? 1 : 0.5 }}>
                                <div style={{ fontWeight: active ? 700 : 500, fontSize: 14, color: active ? G : TX }}>
                                  {t('university.degree.lessonN', 'Lesson {{n}}', { n: lesson.lesson_id })}: {lesson.title}
                                </div>
                                <div style={{ fontSize: 12, color: MU, marginTop: 4 }}>
                                  {lesson.learning_objective}
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                                  {lesson.core_concepts.slice(0, 4).map(c => (
                                    <span key={c} style={{
                                      background: 'rgba(255,255,255,0.06)', border: `1px solid ${BR}`,
                                      borderRadius: 6, padding: '2px 8px', fontSize: 11, color: MU,
                                    }}>
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {active && (
                                <span style={{
                                  background: G, color: '#06060c',
                                  borderRadius: 8, padding: '4px 10px',
                                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                                }}>
                                  {t('university.degree.current', 'CURRENT')}
                                </span>
                              )}
                            </motion.button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
